import {
  MICROSOFT_MAIL_SCOPE,
  microsoftGraphFetch,
  requestMicrosoftAccessToken,
} from "@/lib/microsoftCalendarClient";

const GRAPH_ROOT = "https://graph.microsoft.com/v1.0";
const MESSAGE_FIELDS = [
  "id", "subject", "body", "bodyPreview", "from", "sender", "toRecipients",
  "ccRecipients", "receivedDateTime", "sentDateTime", "isRead", "hasAttachments",
  "importance", "conversationId", "webLink", "parentFolderId",
].join(",");

export const MAILBOX_FOLDERS = [
  { id: "inbox", label: "Inbox" },
  { id: "drafts", label: "Drafts" },
  { id: "sentitems", label: "Sent" },
  { id: "archive", label: "Archive" },
  { id: "deleteditems", label: "Deleted" },
];

export function requestMicrosoftMailAccessToken() {
  return requestMicrosoftAccessToken({
    scope: MICROSOFT_MAIL_SCOPE,
    popupName: "clsConnectMicrosoftMail",
  });
}

export async function getMicrosoftMailboxProfile(accessToken) {
  return microsoftGraphFetch(accessToken, `${GRAPH_ROOT}/me?$select=displayName,mail,userPrincipalName`);
}

export async function listMicrosoftMailFolders(accessToken) {
  const payload = await microsoftGraphFetch(
    accessToken,
    `${GRAPH_ROOT}/me/mailFolders?$top=100&$select=id,displayName,parentFolderId,totalItemCount,unreadItemCount,childFolderCount`
  );
  return payload.value || [];
}

export async function listMicrosoftMessages(accessToken, { folderId = "inbox", search = "", top = 50 } = {}) {
  const url = new URL(`${GRAPH_ROOT}/me/mailFolders/${encodeURIComponent(folderId)}/messages`);
  url.searchParams.set("$top", String(Math.min(Math.max(top, 1), 100)));
  url.searchParams.set("$select", MESSAGE_FIELDS);
  url.searchParams.set("$orderby", "receivedDateTime desc");
  if (search.trim()) {
    url.searchParams.set("$search", `\"${search.trim().replace(/\"/g, "")}\"`);
    url.searchParams.delete("$orderby");
  }
  const payload = await microsoftGraphFetch(accessToken, url.toString(), {
    headers: { Prefer: 'outlook.body-content-type="text"' },
  });
  return { messages: payload.value || [], nextLink: payload["@odata.nextLink"] || null };
}

export async function getMicrosoftMessage(accessToken, messageId) {
  return microsoftGraphFetch(
    accessToken,
    `${GRAPH_ROOT}/me/messages/${encodeURIComponent(messageId)}?$select=${MESSAGE_FIELDS}`,
    { headers: { Prefer: 'outlook.body-content-type="text"' } }
  );
}

export async function listMicrosoftMessageAttachments(accessToken, messageId) {
  const payload = await microsoftGraphFetch(
    accessToken,
    `${GRAPH_ROOT}/me/messages/${encodeURIComponent(messageId)}/attachments?$select=id,name,contentType,size,isInline`
  );
  return payload.value || [];
}

export async function downloadMicrosoftAttachment(accessToken, messageId, attachmentId) {
  const attachment = await microsoftGraphFetch(
    accessToken,
    `${GRAPH_ROOT}/me/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`
  );
  if (!attachment.contentBytes) throw new Error("This attachment cannot be downloaded directly.");
  return attachment;
}

export function recipient(address) {
  return { emailAddress: { address: address.trim() } };
}

export async function sendMicrosoftMessage(accessToken, { to = [], cc = [], subject = "", body = "", attachments = [] }) {
  return microsoftGraphFetch(accessToken, `${GRAPH_ROOT}/me/sendMail`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      message: {
        subject,
        body: { contentType: "Text", content: body },
        toRecipients: to.map(recipient),
        ccRecipients: cc.map(recipient),
        attachments: attachments.map((item) => ({
          "@odata.type": "#microsoft.graph.fileAttachment",
          name: item.name,
          contentType: item.contentType || "application/octet-stream",
          contentBytes: item.contentBytes,
        })),
      },
      saveToSentItems: true,
    }),
  });
}

export async function replyToMicrosoftMessage(accessToken, messageId, comment, replyAll = false) {
  return microsoftGraphFetch(
    accessToken,
    `${GRAPH_ROOT}/me/messages/${encodeURIComponent(messageId)}/${replyAll ? "replyAll" : "reply"}`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ comment }) }
  );
}

export async function forwardMicrosoftMessage(accessToken, messageId, to, comment = "") {
  return microsoftGraphFetch(accessToken, `${GRAPH_ROOT}/me/messages/${encodeURIComponent(messageId)}/forward`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comment, toRecipients: to.map(recipient) }),
  });
}

export async function setMicrosoftMessageRead(accessToken, messageId, isRead) {
  return microsoftGraphFetch(accessToken, `${GRAPH_ROOT}/me/messages/${encodeURIComponent(messageId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ isRead }),
  });
}

export async function moveMicrosoftMessage(accessToken, messageId, destinationId) {
  return microsoftGraphFetch(accessToken, `${GRAPH_ROOT}/me/messages/${encodeURIComponent(messageId)}/move`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ destinationId }),
  });
}

export async function deleteMicrosoftMessage(accessToken, messageId) {
  return microsoftGraphFetch(accessToken, `${GRAPH_ROOT}/me/messages/${encodeURIComponent(messageId)}`, { method: "DELETE" });
}
